import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

import { Subscription } from 'rxjs';
import {
  DialogService,
  DialogConfig,
} from '../../../core/services/dialog.service';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible) {
      <div
        class="dialog-backdrop"
        (click)="onCancel()"
        (keydown.escape)="onCancel()"
        tabindex="-1">
        <div
          class="dialog-box"
          (click)="$event.stopPropagation()"
          (keydown)="$event.stopPropagation()"
          role="dialog"
          [attr.aria-modal]="true">
          <h2 class="dialog-title">{{ config!.title }}</h2>
          <p class="dialog-message">{{ config!.message }}</p>
          <div class="dialog-actions">
            @if (config!.type === 'confirm') {
              <button class="dialog-btn dialog-btn-cancel" (click)="onCancel()">
                {{ config!.cancelText ?? 'Cancel' }}
              </button>
            }
            <button class="dialog-btn dialog-btn-ok" (click)="onConfirm()">
              {{ config!.confirmText ?? 'OK' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .dialog-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }
      .dialog-box {
        background: white;
        border-radius: 8px;
        padding: 28px 32px;
        min-width: 340px;
        max-width: 480px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
      }
      .dialog-title {
        font-size: 16px;
        font-weight: 700;
        color: var(--ukg-gray-900, #1a1a2e);
        margin: 0 0 12px 0;
      }
      .dialog-message {
        font-size: 14px;
        color: var(--ukg-gray-700, #374151);
        margin: 0 0 24px 0;
        line-height: 1.5;
      }
      .dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
      }
      .dialog-btn {
        padding: 8px 20px;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        border: 1px solid transparent;
      }
      .dialog-btn-cancel {
        background: white;
        border-color: var(--ukg-gray-300, #d1d5db);
        color: var(--ukg-gray-700, #374151);
      }
      .dialog-btn-cancel:hover {
        background: var(--ukg-gray-50, #f9fafb);
      }
      .dialog-btn-ok {
        background: #005151;
        color: white;
      }
      .dialog-btn-ok:hover {
        background: #003d3d;
      }
    `,
  ],
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  private readonly dialogService = inject(DialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  visible = false;
  config: DialogConfig | null = null;

  ngOnInit(): void {
    this.sub = this.dialogService.dialog$.subscribe(config => {
      this.config = config;
      this.visible = true;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onConfirm(): void {
    this.visible = false;
    this.dialogService.sendResult(true);
    this.cdr.markForCheck();
  }

  onCancel(): void {
    this.visible = false;
    this.dialogService.sendResult(false);
    this.cdr.markForCheck();
  }
}
